import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { CurrencyInput } from 'ui-components/components/currency-input';
import { DropdownMenu } from 'ui-components/components/dropdown-menu/';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexRow } from 'ui-components/components/flex';
import { InlineMessage } from 'ui-components/components/inline-message';
import { PercentageInput } from 'ui-components/components/percentage-input';
import { Text, TextType } from 'ui-components/components/text';
import { emptyObject, emptyArray } from 'ui-components/default-props/empty';

import style from './edit-delivery-option-page.module.less';
import Global from '../../../../../../store/global-selectors';
import messages from '../../messages';
import DeliveryOptionsItemPropType from '../delivery-option-item-prop-type';
import { fallbackOptionModeEnum } from '../enums';
import InfoTooltip from '../info-tooltip';

const FreeFallBackWarning = React.memo(() => {
  return (
    <div className={style['fallback-warning']}>
      <InlineMessage type='warning'>
        <FormattedMessage {...messages.freeShippingFallbackWarning} />
      </InlineMessage>
    </div>
  );
});

export const FallbackOptions = ({
  fallbackOptionObject = emptyObject,
  fallbackOption = emptyObject,
  fallbackOptions = emptyArray,
  setItem,
  item,
}) => {
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const onFallbackOptionModeChangeCallback = useCallback(
    (mode) => {
      setItem({
        ...item,
        fallbackOption: {
          mode,
          cartPercentage: 0,
          flatRate: {
            value: 0,
            code: sellerCurrencyCode,
          },
        },
      });
    },
    [item, sellerCurrencyCode, setItem],
  );
  const onFallbackOptionFlatRateValueCallback = useCallback(
    (value) => {
      setItem({
        ...item,
        fallbackOption: {
          ...item.fallbackOption,
          flatRate: {
            value: parseFloat(value),
            code: sellerCurrencyCode,
          },
        },
      });
    },
    [item, sellerCurrencyCode, setItem],
  );
  const onFallbackOptionCartPercentageValueCallback = useCallback(
    (percentage) => {
      setItem({
        ...item,
        fallbackOption: {
          ...item.fallbackOption,
          cartPercentage: percentage,
        },
      });
    },
    [item, setItem],
  );

  return (
    <div className={style['edit-delivery-options-section']}>
      <div className={style['live-rate-section']}>
        <FlexRow>
          <Text className={style['fallback-dropdown']} textType={TextType.Paragraph} value={messages.ifRatesUnavailable} />
          <Text className={style.required} textType={TextType.Paragraph} value={messages.required} />
        </FlexRow>
        <DropdownMenu className={style['section-control-dropdown-menu']} dropdownMenuShouldHaveWidthOfButton>
          <DropdownMenuToggler className={style['section-control-dropdown-button']} theme='white'>
            {fallbackOptionObject.displayName ?? <FormattedMessage {...messages.selectAction} />}
          </DropdownMenuToggler>
          {fallbackOptions.map((option) => (
            <DropdownMenuItem key={option.id} name={option.id} onClick={onFallbackOptionModeChangeCallback}>
              {option.displayName}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
      </div>
      {fallbackOption.mode === fallbackOptionModeEnum.flatRate && (
        <div className={style['live-rate-section']}>
          <div className={style['live-rate-title']}>
            <FormattedMessage {...messages.enterAFlatRate} />
          </div>
          <CurrencyInput
            className={style['currency-input']}
            currencyCode={sellerCurrencyCode}
            onChange={onFallbackOptionFlatRateValueCallback}
            onlyPositive
            value={fallbackOption.flatRate.value}
          />
          {fallbackOption.flatRate.value === 0 && <FreeFallBackWarning />}
        </div>
      )}
      {fallbackOption.mode === fallbackOptionModeEnum.cartPercentage && (
        <div className={style['live-rate-section']}>
          <div className={style['live-rate-title']}>
            <FormattedMessage {...messages.enterAPercentageOfCart} />
          </div>
          <div className={style['fallback-options-percentage-controls']}>
            <PercentageInput
              className={style['percentage-input']}
              onChange={onFallbackOptionCartPercentageValueCallback}
              value={fallbackOption.cartPercentage}
            />
            <div>
              <FormattedMessage {...messages.howCalculated} />
            </div>
            <InfoTooltip message={<FormattedMessage {...messages.howCalculatedExplanation} />} />
          </div>
          {fallbackOption.cartPercentage === 0 && <FreeFallBackWarning />}
        </div>
      )}
    </div>
  );
};

FallbackOptions.propTypes = {
  fallbackOption: PropTypes.shape({
    cartPercentage: PropTypes.number,
    flatRate: PropTypes.shape({
      value: PropTypes.number,
    }),
    mode: PropTypes.string,
  }),
  fallbackOptionObject: PropTypes.shape({
    displayName: PropTypes.shape({
      defaultMessage: PropTypes.string,
    }),
  }),
  fallbackOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      displayName: PropTypes.shape({
        defaultMessage: PropTypes.string,
      }),
    }),
  ),
  item: DeliveryOptionsItemPropType.isRequired,
  setItem: PropTypes.func.isRequired,
};
